import React, { useState, useEffect, useCallback } from 'react';
import {
  Database, Download, RefreshCw, Search,
  CheckSquare, Square, FileSpreadsheet,
  Calendar, ChevronLeft, ChevronRight, Phone,
  ShoppingCart, X, Gift
} from 'lucide-react';
import api from '../services/api';

export default function DataManager() {
  const [data, setData] = useState([]);
  const [stats, setStats] = useState({ total_success: 0, not_downloaded: 0, downloaded: 0, with_gsm: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [downloadFilter, setDownloadFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showManualSale, setShowManualSale] = useState(false);
  const [manualSaleData, setManualSaleData] = useState({ quantity: 100, telegram_username: '', notes: '', include_bonus: true });
  const [manualSaleLoading, setManualSaleLoading] = useState(false);
  const [manualSaleResult, setManualSaleResult] = useState(null);
  const [goToPage, setGoToPage] = useState('');

  const fetchStats = async () => {
    try {
      const res = await api.get('/data/stats');
      if (res.data.success) setStats(res.data.stats);
    } catch (err) {
      console.error('Stats error:', err);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page, per_page: perPage, downloaded: downloadFilter,
        ...(search && { search }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo }),
        sort_by: sortBy, sort_order: sortOrder
      });
      const res = await api.get("/data/success?" + params);
      if (res.data.success) {
        setData(res.data.items);
        setTotal(res.data.total);
        setTotalPages(res.data.pages);
      }
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search, downloadFilter, dateFrom, dateTo, sortBy, sortOrder]);

  useEffect(() => {
    fetchStats();
    fetchData();
  }, [fetchData]);

  const handleSelectAll = () => {
    if (selectAll) setSelectedIds([]);
    else setSelectedIds(data.map(item => item.id));
    setSelectAll(!selectAll);
  };

  const handleSelect = (id) => {
    if (selectedIds.includes(id)) setSelectedIds(selectedIds.filter(i => i !== id));
    else setSelectedIds([...selectedIds, id]);
  };

  const handleGoToPage = (e) => {
    e.preventDefault();
    const pageNum = parseInt(goToPage);
    if (pageNum >= 1 && pageNum <= totalPages) {
      setPage(pageNum);
      setGoToPage('');
    }
  };

  const handleManualSale = async () => {
    if (!manualSaleData.telegram_username.trim()) { alert('Telegram username gerekli!'); return; }
    if (manualSaleData.quantity < 1) { alert('Geçerli bir miktar girin!'); return; }
    setManualSaleLoading(true);
    try {
      const res = await api.post('/order/manual-sale', manualSaleData);
      if (res.data.success) {
        setManualSaleResult(res.data);
        fetchStats();
        fetchData();
      } else {
        alert(res.data.error || 'Hata oluştu');
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Satış hatası!');
    } finally {
      setManualSaleLoading(false);
    }
  };

  const handleDownloadManualSale = async (orderCode) => {
    try {
      const res = await api.get(`/order/manual-sale/download/${orderCode}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `manual_sale_${orderCode}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setShowManualSale(false);
      setManualSaleResult(null);
      setManualSaleData({ quantity: 100, telegram_username: '', notes: '', include_bonus: true });
    } catch (err) {
      alert('İndirme hatası!');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl shadow-lg">
            <Database className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Veri Yönetimi</h1>
            <p className="text-gray-400 text-sm">Başarılı test sonuçları</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowManualSale(true)} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-red-600 rounded-lg font-bold text-white hover:from-orange-600 hover:to-red-700">
            <ShoppingCart className="w-5 h-5" /> Manuel Satış
          </button>
          <button onClick={() => { fetchStats(); fetchData(); }} className="p-2 bg-gray-800 rounded-lg hover:bg-gray-700">
            <RefreshCw className={`w-5 h-5 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
          <div className="text-3xl font-bold text-emerald-400">{stats.total_success.toLocaleString()}</div>
          <div className="text-gray-400 text-sm">Toplam Başarılı</div>
        </div>
        <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
          <div className="text-3xl font-bold text-cyan-400">{stats.not_downloaded.toLocaleString()}</div>
          <div className="text-gray-400 text-sm">Stok (Satılmamış)</div>
        </div>
        <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
          <div className="text-3xl font-bold text-purple-400">{stats.downloaded.toLocaleString()}</div>
          <div className="text-gray-400 text-sm">Satılmış</div>
        </div>
        <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
          <div className="text-3xl font-bold text-orange-400">{stats.with_gsm.toLocaleString()}</div>
          <div className="text-gray-400 text-sm">GSM Eşleşmiş</div>
        </div>
      </div>

      <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input type="text" placeholder="TC veya isim ara..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 pl-10 pr-4 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500" />
          </div>
          <select value={downloadFilter} onChange={(e) => { setDownloadFilter(e.target.value); setPage(1); }}
            className="bg-gray-800 border border-gray-700 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-cyan-500">
            <option value="all">Tümü</option>
            <option value="no">Stokta</option>
            <option value="yes">Satılmış</option>
          </select>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="bg-gray-800 border border-gray-700 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-cyan-500" />
            <span className="text-gray-500">-</span>
            <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="bg-gray-800 border border-gray-700 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-cyan-500" />
          </div>
        </div>
      </div>

      <div className="bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800/50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <button onClick={handleSelectAll} className="text-gray-400 hover:text-white">
                    {selectAll ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase cursor-pointer hover:text-white" onClick={() => handleSort('tc')}>
                  TC {sortBy === 'tc' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Şifre</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Ad Soyad</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Doğum Tarihi</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Doğum Yeri</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">GSM</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase cursor-pointer hover:text-white" onClick={() => handleSort('downloaded_at')}>
                  Durum {sortBy === 'downloaded_at' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase cursor-pointer hover:text-white" onClick={() => handleSort('updated_at')}>
                  Tarih {sortBy === 'updated_at' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {loading ? (
                <tr><td colSpan="9" className="px-4 py-8 text-center text-gray-500"><RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />Yükleniyor...</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan="9" className="px-4 py-8 text-center text-gray-500">Kayıt bulunamadı</td></tr>
              ) : data.map((item) => (
                <tr key={item.id} className="hover:bg-gray-800/30 transition-colors">
                  <td className="px-4 py-3">
                    <button onClick={() => handleSelect(item.id)} className="text-gray-400 hover:text-white">
                      {selectedIds.includes(item.id) ? <CheckSquare className="w-5 h-5 text-cyan-400" /> : <Square className="w-5 h-5" />}
                    </button>
                  </td>
                  <td className="px-4 py-3 font-mono text-sm text-white">{item.tc}</td>
                  <td className="px-4 py-3 font-mono text-sm text-emerald-400">{item.password}</td>
                  <td className="px-4 py-3 text-sm text-gray-300">{item.ad_soyad}</td>
                  <td className="px-4 py-3 text-sm text-yellow-400">{item.dogum_tarihi || '-'}</td>
                  <td className="px-4 py-3 text-sm text-blue-400">{item.dogum_yeri || '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    {item.gsm_numbers?.length > 0 ? <span className="text-orange-400">{item.gsm_numbers.join(', ')}</span> : <span className="text-gray-600">-</span>}
                  </td>
                  <td className="px-4 py-3">
                    {item.downloaded_at ? (
                      <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs rounded-full">Satıldı</span>
                    ) : (
                      <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 text-xs rounded-full">Stokta</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{item.updated_at ? new Date(item.updated_at).toLocaleDateString('tr-TR') : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-800">
          <div className="text-sm text-gray-400">Toplam {total.toLocaleString()} kayıt, Sayfa {page}/{totalPages}</div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(1)} disabled={page === 1} className="px-2 py-1 bg-gray-800 rounded-lg hover:bg-gray-700 disabled:opacity-50 text-gray-400 text-sm">
              İlk
            </button>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 bg-gray-800 rounded-lg hover:bg-gray-700 disabled:opacity-50">
              <ChevronLeft className="w-4 h-4 text-gray-400" />
            </button>
            <form onSubmit={handleGoToPage} className="flex items-center gap-1">
              <input type="number" value={goToPage} onChange={(e) => setGoToPage(e.target.value)} placeholder={page}
                className="w-16 px-2 py-1 bg-gray-800 border border-gray-700 rounded-lg text-white text-center text-sm focus:outline-none focus:border-cyan-500" min="1" max={totalPages} />
              <span className="text-gray-500 text-sm">/ {totalPages}</span>
            </form>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 bg-gray-800 rounded-lg hover:bg-gray-700 disabled:opacity-50">
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </button>
            <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className="px-2 py-1 bg-gray-800 rounded-lg hover:bg-gray-700 disabled:opacity-50 text-gray-400 text-sm">
              Son
            </button>
          </div>
        </div>
      </div>

      {showManualSale && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <ShoppingCart className="w-6 h-6 text-orange-400" /> Manuel Satış
              </h2>
              <button onClick={() => { setShowManualSale(false); setManualSaleResult(null); }} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            {!manualSaleResult ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Miktar</label>
                  <input type="number" value={manualSaleData.quantity} onChange={(e) => setManualSaleData({...manualSaleData, quantity: parseInt(e.target.value) || 0})}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 px-3 text-white" min="1" />
                  <div className="text-xs text-gray-500 mt-1">Stok: {stats.not_downloaded}</div>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Telegram Username</label>
                  <input type="text" value={manualSaleData.telegram_username} onChange={(e) => setManualSaleData({...manualSaleData, telegram_username: e.target.value})}
                    placeholder="@username" className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 px-3 text-white" />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Not (Opsiyonel)</label>
                  <textarea value={manualSaleData.notes} onChange={(e) => setManualSaleData({...manualSaleData, notes: e.target.value})}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 px-3 text-white h-20" placeholder="Sipariş notu..." />
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                  <input type="checkbox" id="includeBonus" checked={manualSaleData.include_bonus}
                    onChange={(e) => setManualSaleData({...manualSaleData, include_bonus: e.target.checked})} className="w-4 h-4 accent-orange-500" />
                  <label htmlFor="includeBonus" className="text-gray-300 flex items-center gap-2">
                    <Gift className="w-4 h-4 text-orange-400" /> %10 Hediye Ekle ({Math.floor(manualSaleData.quantity * 0.1)} adet)
                  </label>
                </div>
                <div className="p-3 bg-gray-800/50 rounded-lg">
                  <div className="flex justify-between text-sm"><span className="text-gray-400">Sipariş:</span><span className="text-white">{manualSaleData.quantity} adet</span></div>
                  {manualSaleData.include_bonus && <div className="flex justify-between text-sm"><span className="text-gray-400">Hediye (+10%):</span><span className="text-orange-400">+{Math.floor(manualSaleData.quantity * 0.1)} adet</span></div>}
                  <div className="flex justify-between text-sm font-bold mt-2 pt-2 border-t border-gray-700">
                    <span className="text-gray-300">Toplam Teslim:</span>
                    <span className="text-emerald-400">{manualSaleData.quantity + (manualSaleData.include_bonus ? Math.floor(manualSaleData.quantity * 0.1) : 0)} adet</span>
                  </div>
                </div>
                <button onClick={handleManualSale} disabled={manualSaleLoading || !manualSaleData.telegram_username}
                  className="w-full py-3 bg-gradient-to-r from-orange-500 to-red-600 rounded-lg font-bold text-white hover:from-orange-600 hover:to-red-700 disabled:opacity-50">
                  {manualSaleLoading ? 'İşleniyor...' : 'Satışı Tamamla'}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-center p-4 bg-emerald-500/20 rounded-lg">
                  <div className="text-emerald-400 text-lg font-bold mb-2">✅ Satış Tamamlandı!</div>
                  <div className="text-gray-300">Sipariş Kodu: <span className="font-mono text-white">{manualSaleResult.order_code}</span></div>
                </div>
                <div className="p-3 bg-gray-800 rounded-lg space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-gray-400">Sipariş:</span><span className="text-white">{manualSaleResult.quantity} adet</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-400">Hediye:</span><span className="text-orange-400">+{manualSaleResult.bonus} adet</span></div>
                  <div className="flex justify-between text-sm font-bold pt-2 border-t border-gray-700">
                    <span className="text-gray-300">Toplam:</span><span className="text-emerald-400">{manualSaleResult.total_delivered} adet</span>
                  </div>
                </div>
                <button onClick={() => handleDownloadManualSale(manualSaleResult.order_code)}
                  className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg font-bold text-white hover:from-cyan-600 hover:to-blue-700 flex items-center justify-center gap-2">
                  <Download className="w-5 h-5" /> Excel İndir
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
